import { z } from 'zod';

/**
 * Faults of a store, and the store's own service calls (adr/0036, docs/SERVICE_TICKETS.md).
 * A ticket comes from one of two places:
 * - SCAN: the platform's automatic scan found something wrong with the store (one ticket per
 *   check per store; it closes by itself when the scan no longer finds it, and opens again when
 *   it comes back)
 * - STORE: the store's owner or staff opened a service call from the store admin
 * The platform staff see both on the store's page; the store sees its own calls only.
 */

export const SERVICE_TICKET_KINDS = ['SECURITY', 'GENERAL'] as const;
export const serviceTicketKindSchema = z.enum(SERVICE_TICKET_KINDS);
export type ServiceTicketKind = z.infer<typeof serviceTicketKindSchema>;

export const SERVICE_TICKET_SOURCES = ['SCAN', 'STORE'] as const;
export const serviceTicketSourceSchema = z.enum(SERVICE_TICKET_SOURCES);
export type ServiceTicketSource = z.infer<typeof serviceTicketSourceSchema>;

export const SERVICE_TICKET_STATUSES = ['OPEN', 'IN_PROGRESS', 'RESOLVED'] as const;
export const serviceTicketStatusSchema = z.enum(SERVICE_TICKET_STATUSES);
export type ServiceTicketStatus = z.infer<typeof serviceTicketStatusSchema>;

export const SERVICE_TICKET_SEVERITIES = ['LOW', 'MEDIUM', 'HIGH'] as const;
export const serviceTicketSeveritySchema = z.enum(SERVICE_TICKET_SEVERITIES);
export type ServiceTicketSeverity = z.infer<typeof serviceTicketSeveritySchema>;

/**
 * What the automatic scan checks in every store.
 * - ACCOUNT_LOCKED: a member of the store is locked out after repeated wrong passwords
 * - FAILED_SIGN_INS: a member has several wrong passwords in a row (someone may be guessing)
 * - CERTIFICATE_EXPIRING: the HTTPS certificate of a serving address ends soon
 * - OWNER_WITHOUT_PASSWORD: the store's owner never set a password
 * - DOMAIN_PROBLEM: an own address of the store failed its checks or stopped pointing at us
 * - MAIL_FAILED: the store's Gmail account stopped sending its new requests
 * - NO_CONTACT: the store has neither a phone nor a WhatsApp number for shoppers
 * - SETUP_FAILED: the store's setup stopped on an error
 * - COPY_FAILED: product copy failed to be written in the last week
 */
export const SERVICE_CHECKS = [
  'ACCOUNT_LOCKED',
  'FAILED_SIGN_INS',
  'CERTIFICATE_EXPIRING',
  'OWNER_WITHOUT_PASSWORD',
  'DOMAIN_PROBLEM',
  'MAIL_FAILED',
  'NO_CONTACT',
  'SETUP_FAILED',
  'COPY_FAILED',
] as const;
export const serviceCheckSchema = z.enum(SERVICE_CHECKS);
export type ServiceCheck = z.infer<typeof serviceCheckSchema>;

/** Each check's kind and how serious it is; a finding takes both from here. */
export const SERVICE_CHECK_RULES = {
  ACCOUNT_LOCKED: { kind: 'SECURITY', severity: 'HIGH' },
  FAILED_SIGN_INS: { kind: 'SECURITY', severity: 'MEDIUM' },
  CERTIFICATE_EXPIRING: { kind: 'SECURITY', severity: 'HIGH' },
  OWNER_WITHOUT_PASSWORD: { kind: 'SECURITY', severity: 'LOW' },
  DOMAIN_PROBLEM: { kind: 'GENERAL', severity: 'HIGH' },
  MAIL_FAILED: { kind: 'GENERAL', severity: 'HIGH' },
  NO_CONTACT: { kind: 'GENERAL', severity: 'MEDIUM' },
  SETUP_FAILED: { kind: 'GENERAL', severity: 'HIGH' },
  COPY_FAILED: { kind: 'GENERAL', severity: 'LOW' },
} as const satisfies Record<
  ServiceCheck,
  { kind: ServiceTicketKind; severity: ServiceTicketSeverity }
>;

/** A call the store opens is serious by its kind: a security call is HIGH, any other MEDIUM. */
export function storeCallSeverity(kind: ServiceTicketKind): ServiceTicketSeverity {
  return kind === 'SECURITY' ? 'HIGH' : 'MEDIUM';
}

/**
 * What the scan saw, for the screen to say it: counts, a hostname, a date. Safe values only,
 * never a password or anything the shopper wrote.
 */
export const serviceFactsSchema = z.record(z.string(), z.union([z.string(), z.number()]));
export type ServiceFacts = z.infer<typeof serviceFactsSchema>;

export const SERVICE_SUBJECT_MAX = 150;
export const SERVICE_MESSAGE_MAX = 4000;

export const SERVICE_AUTHORS = ['STORE', 'PLATFORM'] as const;
export const serviceAuthorSchema = z.enum(SERVICE_AUTHORS);
export type ServiceAuthor = z.infer<typeof serviceAuthorSchema>;

export const serviceTicketSchema = z.object({
  id: z.uuid(),
  kind: serviceTicketKindSchema,
  source: serviceTicketSourceSchema,
  severity: serviceTicketSeveritySchema,
  status: serviceTicketStatusSchema,
  /** The scan's check (SCAN only). */
  check: serviceCheckSchema.nullable(),
  facts: serviceFactsSchema,
  /** The store's own words (STORE only). */
  subject: z.string().nullable(),
  createdAt: z.iso.datetime(),
  /** The last thing that happened: a message, a change of status, the scan. */
  updatedAt: z.iso.datetime(),
  /** When the scan last saw the problem (SCAN only). */
  lastSeenAt: z.iso.datetime().nullable(),
  resolvedAt: z.iso.datetime().nullable(),
  /** RESOLVED by the scan itself, because the problem is gone (SCAN only). */
  resolvedByScan: z.boolean(),
  messageCount: z.number().int().nonnegative(),
});
export type ServiceTicket = z.infer<typeof serviceTicketSchema>;

export const serviceMessageSchema = z.object({
  id: z.uuid(),
  author: serviceAuthorSchema,
  authorName: z.string().nullable(),
  body: z.string(),
  createdAt: z.iso.datetime(),
});
export type ServiceMessage = z.infer<typeof serviceMessageSchema>;

export const serviceTicketDetailSchema = z.object({
  ticket: serviceTicketSchema,
  messages: z.array(serviceMessageSchema),
});
export type ServiceTicketDetail = z.infer<typeof serviceTicketDetailSchema>;

/** At most this many tickets come back in a list; the open ones first. */
export const SERVICE_TICKET_LIST_MAX = 200;

export const serviceTicketListSchema = z.object({
  tickets: z.array(serviceTicketSchema),
  /** When the scan last went over this store; null before its first time. */
  scannedAt: z.iso.datetime().nullable(),
});
export type ServiceTicketList = z.infer<typeof serviceTicketListSchema>;

const messageBodySchema = z.string().trim().min(1).max(SERVICE_MESSAGE_MAX);

export const openServiceCallRequestSchema = z.object({
  kind: serviceTicketKindSchema,
  subject: z.string().trim().min(2).max(SERVICE_SUBJECT_MAX),
  body: messageBodySchema,
});
export type OpenServiceCallRequest = z.infer<typeof openServiceCallRequestSchema>;

export const serviceMessageRequestSchema = z.object({ body: messageBodySchema });
export type ServiceMessageRequest = z.infer<typeof serviceMessageRequestSchema>;

export const serviceStatusRequestSchema = z.object({ status: serviceTicketStatusSchema });
export type ServiceStatusRequest = z.infer<typeof serviceStatusRequestSchema>;

export const serviceTicketParamsSchema = z.object({ ticketId: z.uuid() });
export const tenantServiceTicketParamsSchema = z.object({
  tenantId: z.uuid(),
  ticketId: z.uuid(),
});

/** One open ticket in the platform's list across all stores. */
export const platformServiceTicketSchema = z.object({
  id: z.uuid(),
  tenantId: z.uuid(),
  tenantName: z.string(),
  kind: serviceTicketKindSchema,
  source: serviceTicketSourceSchema,
  severity: serviceTicketSeveritySchema,
  status: serviceTicketStatusSchema,
  check: serviceCheckSchema.nullable(),
  facts: serviceFactsSchema,
  subject: z.string().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type PlatformServiceTicket = z.infer<typeof platformServiceTicketSchema>;

export const platformServiceTicketsSchema = z.object({
  tickets: z.array(platformServiceTicketSchema),
  /** More open tickets exist than the list shows. */
  truncated: z.boolean(),
});
export type PlatformServiceTickets = z.infer<typeof platformServiceTicketsSchema>;

/** Sorts open tickets first, then the serious ones, then the newest activity. */
export function compareServiceTickets(
  a: Pick<ServiceTicket, 'status' | 'severity' | 'updatedAt'>,
  b: Pick<ServiceTicket, 'status' | 'severity' | 'updatedAt'>,
): number {
  const open = (ticket: { status: ServiceTicketStatus }) => (ticket.status === 'RESOLVED' ? 1 : 0);
  const weight = (ticket: { severity: ServiceTicketSeverity }) =>
    SERVICE_TICKET_SEVERITIES.length - SERVICE_TICKET_SEVERITIES.indexOf(ticket.severity);
  return (
    open(a) - open(b) ||
    weight(a) - weight(b) ||
    (a.updatedAt < b.updatedAt ? 1 : a.updatedAt > b.updatedAt ? -1 : 0)
  );
}
